/**
 * client/src/features/income/components/AddIncome/BucketOwnerSelector.jsx
 * 
 * Component for selecting bucket and owner when creating income.
 * Allows users to select their own buckets or partner's buckets,
 * and assign income to themselves or their partner.
 */
import React, { useState, useEffect } from 'react';
import { useAuthContext } from '../../../auth/context/AuthContext';
import useBuckets from '../../hooks/useBuckets';
import { UserIcon, UserGroupIcon } from '@heroicons/react/24/outline';

const BucketOwnerSelector = ({ 
  selectedBucketId, 
  selectedOwnerId,
  onBucketChange, 
  onOwnerChange,
  disabled = false 
}) => {
  const { user } = useAuthContext();
  const { buckets, loading } = useBuckets({ includePartner: true });
  
  const [localBuckets, setLocalBuckets] = useState([]);
  const [localOwnerId, setLocalOwnerId] = useState(selectedOwnerId || user?._id || user?.id);
  
  // Check if user has a partner
  const hasPartner = !!(user?.partner || user?.partnerId || user?.createdPartner);
  const partnerId = user?.partner?.id || user?.partnerId?._id || user?.partnerId;
  const partnerName = user?.partner?.firstName || user?.createdPartner?.firstName || 'Partner';
  const userName = user?.firstName || 'You';

  // Separate buckets by owner
  useEffect(() => {
    if (buckets && buckets.length > 0) {
      const userBuckets = buckets.filter(b => !b.isPartnerBucket);
      const partnerBuckets = buckets.filter(b => b.isPartnerBucket);
      
      setLocalBuckets([
        ...userBuckets.map(b => ({ ...b, ownerLabel: userName, ownerId: user?._id || user?.id })),
        ...partnerBuckets.map(b => ({ ...b, ownerLabel: partnerName, ownerId: partnerId }))
      ]);
    }
  }, [buckets, userName, partnerName, partnerId, user]);

  // Filter buckets based on selected owner
  const availableBuckets = localBuckets.filter(b => {
    if (!localOwnerId) return true;
    // FIXED: Only show buckets that belong to the selected owner
    // Compare both ownerId (added by us) and the actual user field from DB
    const bucketOwnerId = b.user?._id || b.user || b.ownerId;
    return bucketOwnerId?.toString() === localOwnerId?.toString();
  });
  
  // Debug logging
  console.log('🔍 BucketOwnerSelector - Filtering buckets:', {
    localOwnerId,
    totalBuckets: localBuckets.length,
    availableBuckets: availableBuckets.length,
    bucketDetails: availableBuckets.map(b => ({
      _id: b._id,
      name: b.name,
      user: b.user,
      ownerId: b.ownerId,
      isPartnerBucket: b.isPartnerBucket
    }))
  });

  const handleOwnerChange = (newOwnerId) => {
    setLocalOwnerId(newOwnerId);
    if (onOwnerChange) {
      onOwnerChange(newOwnerId);
    }
    // Reset bucket selection when owner changes
    if (onBucketChange) {
      onBucketChange(null);
    }
  };

  const handleBucketChange = (bucketId) => {
    if (onBucketChange) {
      onBucketChange(bucketId);
    }
  };

  if (!hasPartner && !disabled) {
    // If no partner, don't show the selector, just use the default bucket
    return null;
  }

  return (
    <div className="space-y-4 mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
      <h3 className="text-sm font-semibold text-gray-700 mb-3">Income Assignment</h3>
      
      {/* Owner Selection */}
      {hasPartner && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            This income belongs to:
          </label>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => handleOwnerChange(user?._id || user?.id)}
              disabled={disabled}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                localOwnerId === (user?._id || user?.id)
                  ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                  : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
              } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <UserIcon className="h-5 w-5" />
              <span className="font-medium">{userName}</span>
            </button>
            <button
              type="button"
              onClick={() => handleOwnerChange(partnerId)}
              disabled={disabled || !partnerId}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                localOwnerId === partnerId
                  ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                  : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
              } ${disabled || !partnerId ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <UserGroupIcon className="h-5 w-5" />
              <span className="font-medium">{partnerName}</span>
            </button>
          </div>
        </div>
      )}

      {/* Bucket Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Select Bucket:
        </label>
        {loading ? (
          <div className="text-sm text-gray-500">Loading buckets...</div>
        ) : availableBuckets.length === 0 ? (
          <div className="text-sm text-gray-500">
            No buckets available. Please create a bucket first.
          </div>
        ) : (
          <select
            value={selectedBucketId || ''}
            onChange={(e) => handleBucketChange(e.target.value)}
            disabled={disabled}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <option value="">Select a bucket...</option>
            {availableBuckets.map(bucket => (
              <option key={bucket._id} value={bucket._id}>
                {bucket.name} {bucket.isPartnerBucket ? `(${partnerName}'s)` : `(${userName}'s)`}
              </option>
            ))}
          </select>
        )}
      </div>
    </div>
  );
};

export default BucketOwnerSelector;

